/**
 * Site-group (plant) scoped layout component that seeds BOTH the Redux customer
 * and plant contexts, and renders its children (via React Router Outlet) once
 * both are ready. No longer requires a CustomerLayout parent — it self-seeds
 * the customer context from the URL params.
 *
 * Compose it as the element on a plant-scoped layout route, with child routes
 * rendered via <Outlet />.
 *
 * Contains:
 * - SiteGroupLayout — site-group-tier layout route component (seeds customer + plant)
 */
import React, { useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import ScopeContext from '@sophocles/app/ScopeContext';
import RouteLoader from '@sophocles/components/App/RouteLoader';
import ScopeLoadError from '@sophocles/components/Layout/ScopeLoadError';
import { resolveScopeLoadState, SCOPE_LOAD_STATE } from '@sophocles/components/Layout/scopeLoadState';
import useCustomer from '@sophocles/hooks/useCustomer';
import useSiteGroup from '@sophocles/hooks/useSiteGroup';

/**
 * @param {{ skipEntityFetches?: boolean }} props
 */
function SiteGroupLayout({ skipEntityFetches = false }) {
  const { cust_id: customerId, sitegroup_id: siteGroupId } = useParams();
  const { customer, loading: customerLoading, error: customerError, reload: reloadCustomer } = useCustomer(customerId);
  const {
    siteGroup,
    loading: siteGroupLoading,
    error: siteGroupError,
    reload: reloadSiteGroup,
  } = useSiteGroup(siteGroupId, { skipEntityFetches });

  // Convert the legacy immutable entities to plain JS at this single boundary so
  // descendant views (via useScope) never touch immutable.js. Memoized on the
  // entity refs (reselect-memoized selectors), so it only re-converts on change.
  const scope = useMemo(
    () => ({
      customer: customer ? customer.toJS() : null,
      siteGroup: siteGroup ? siteGroup.toJS() : null,
    }),
    [customer, siteGroup],
  );

  const customerStoreId = customer.get('id', null);
  const customerReady = customerStoreId != null && String(customerStoreId) === String(customerId);
  const siteGroupStoreId = siteGroup.get('id', null);
  const siteGroupReady = siteGroupStoreId != null && String(siteGroupStoreId) === String(siteGroupId);

  const loadState = resolveScopeLoadState({
    loading: customerLoading || siteGroupLoading,
    error: customerError || siteGroupError,
    ready: customerReady && siteGroupReady,
  });
  if (loadState === SCOPE_LOAD_STATE.ERROR) {
    return (
      <ScopeLoadError
        onRetry={() => {
          reloadCustomer();
          reloadSiteGroup();
        }}
      />
    );
  }
  if (loadState === SCOPE_LOAD_STATE.LOADING) {
    return <RouteLoader showShell />;
  }

  return (
    <ScopeContext.Provider value={scope}>
      <Outlet />
    </ScopeContext.Provider>
  );
}

export default SiteGroupLayout;
